import {
  mysqlTable,
  int,
  bigint,
  smallint,
  char,
  varchar,
  text,
  mediumtext,
  longtext,
  double,
  decimal,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { companies, companyAddresses } from "./tenancy";
import { users } from "./identity";
import {
  projects,
  tasks,
  projectNotes,
  discussions,
  discussionReplies,
  taskComments,
  taskNotes,
} from "./projects";
import { currencies, unitTypes, products } from "./finance";
import { teams } from "./hr";
import { tickets } from "./crm";

/**
 * Batch — Core cross-cutting tables. Ported by reconciling each table's
 * Schema::create + every up() Schema::table ALTER (down() ignored) across the
 * base migration (2018_01_01_000000_create_worksuite_new_table.php) and later
 * dated migrations. Citations in the per-table comments below.
 *
 * NOT FOUND (skipped, per spec): notification_preferences, daily_status_reports
 * — neither exists in any migration or in app/ (grep clean).
 *
 * Cross-batch FKs now wired (lazy arrow .references()): teams (department_id),
 * tasks, projects, products, currencies, unit_types, task_comments, task_notes,
 * project_notes, discussions, discussion_replies, tickets. The only exception is
 * users_chat (mention_users.user_chat_id): legacy declares the FK but the
 * users_chat table is not ported, so that column is kept unconstrained.
 *
 * Money: legacy uses double for order/item amounts; widened to double(30,2) by
 * 2023_12_19_change_column_type_in_order_items_table.php — preserved verbatim.
 */

/** countries — base 2018 L51-59. No id/company_id/timestamps. increments PK. */
export const countries = mysqlTable("countries", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  iso: char("iso", { length: 2 }).notNull(),
  name: varchar("name", { length: 80 }).notNull(),
  nicename: varchar("nicename", { length: 80 }).notNull(),
  iso3: char("iso3", { length: 3 }),
  numcode: smallint("numcode"),
  phonecode: int("phonecode").notNull(),
});

/** custom_field_groups — base 2018 L689-695. increments PK, no timestamps. */
export const customFieldGroups = mysqlTable(
  "custom_field_groups",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    name: varchar("name", { length: 255 }).notNull(),
    model: varchar("model", { length: 255 }),
  },
  (t) => [index("custom_field_groups_model_index").on(t.model)],
);

/**
 * custom_fields — base 2018 L697-709 (export already in create L708).
 * +visible enum('true','false') default 'false' nullable (2023_04_17). No timestamps.
 */
export const customFields = mysqlTable("custom_fields", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  customFieldGroupId: int("custom_field_group_id", { unsigned: true }).references(
    () => customFieldGroups.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  label: varchar("label", { length: 100 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  type: varchar("type", { length: 10 }).notNull(),
  required: mysqlEnum("required", ["yes", "no"]).default("no"),
  values: varchar("values", { length: 5000 }),
  export: boolean("export").default(false),
  visible: mysqlEnum("visible", ["true", "false"]).default("false"),
});

/** custom_fields_data — base 2018 L711-718. increments PK, no company_id, no timestamps. */
export const customFieldsData = mysqlTable(
  "custom_fields_data",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    customFieldId: int("custom_field_id", { unsigned: true })
      .notNull()
      .references(() => customFields.id, { onDelete: "cascade", onUpdate: "cascade" }),
    modelId: int("model_id", { unsigned: true }).notNull(),
    model: varchar("model", { length: 255 }),
    value: varchar("value", { length: 10000 }).notNull(),
  },
  (t) => [index("custom_fields_data_model_index").on(t.model)],
);

/**
 * orders — base 2018 L1230-1253. ALTERs: +order_number bigint→varchar nullable
 * (2022_10_31; retyped 2023_09_25 L46) +original_order_number varchar nullable
 * (2023_09_25 L47) +custom_order_number varchar nullable (2023_03_21)
 * +unit_id bigint unsigned → unit_types SET NULL (2023_02_15)
 * +project_id int unsigned → projects CASCADE (2023_12_26).
 * sub_total/total/discount widened to double(30,2) (2023_12_19).
 */
export const orders = mysqlTable("orders", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  orderNumber: varchar("order_number", { length: 255 }),
  originalOrderNumber: varchar("original_order_number", { length: 255 }),
  clientId: int("client_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → projects
  orderDate: date("order_date").notNull(),
  subTotal: double("sub_total", { precision: 30, scale: 2 }).notNull(),
  discount: double("discount", { precision: 30, scale: 2 }).default(0).notNull(),
  discountType: mysqlEnum("discount_type", ["percent", "fixed"]).default("percent").notNull(),
  total: double("total", { precision: 30, scale: 2 }).notNull(),
  status: mysqlEnum("status", [
    "pending",
    "on-hold",
    "failed",
    "processing",
    "completed",
    "canceled",
    "refunded",
  ])
    .default("pending")
    .notNull(),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → currencies
  showShippingAddress: mysqlEnum("show_shipping_address", ["yes", "no"]).default("no").notNull(),
  note: varchar("note", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  companyAddressId: bigint("company_address_id", { mode: "number", unsigned: true }).references(
    () => companyAddresses.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  unitId: bigint("unit_id", { mode: "number", unsigned: true }).references(() => unitTypes.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }), // FK → unit_types
  customOrderNumber: varchar("custom_order_number", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * order_items — base 2018 L1850-1865. unit_price int→double(30,2),
 * quantity→double(30,2) (2023_12_19). +sku varchar nullable (2024_01_24)
 * +field_order int default 0 (2024_09_13).
 */
export const orderItems = mysqlTable("order_items", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  orderId: bigint("order_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => orders.id, { onDelete: "cascade", onUpdate: "cascade" }),
  productId: int("product_id", { unsigned: true }).references(() => products.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }), // FK → products
  itemName: varchar("item_name", { length: 255 }).notNull(),
  itemSummary: text("item_summary"),
  type: mysqlEnum("type", ["item", "discount", "tax"]).default("item").notNull(),
  quantity: double("quantity", { precision: 30, scale: 2 }).notNull(),
  unitPrice: double("unit_price", { precision: 30, scale: 2 }).notNull(),
  amount: double("amount", { precision: 8, scale: 2 }).notNull(),
  hsnSacCode: varchar("hsn_sac_code", { length: 255 }),
  taxes: varchar("taxes", { length: 255 }),
  sku: varchar("sku", { length: 255 }),
  fieldOrder: int("field_order").default(0).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** order_item_images — base 2018 L1867-1873. increments PK, no company_id. order_item NO ACTION onUpdate. */
export const orderItemImages = mysqlTable(
  "order_item_images",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    orderItemId: bigint("order_item_id", { mode: "number", unsigned: true }).references(
      () => orderItems.id,
      { onDelete: "cascade", onUpdate: "no action" },
    ),
    externalLink: varchar("external_link", { length: 255 }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("order_item_images_order_item_id_index").on(t.orderItemId)],
);

/** weekly_timesheets — 2024_12_17_112002. id() bigint PK; company_id is unsignedInteger (int). */
export const weeklyTimesheets = mysqlTable("weekly_timesheets", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true })
    .notNull()
    .references(() => companies.id, { onDelete: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  approvedBy: int("approved_by", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
  }),
  weekStartDate: date("week_start_date").notNull(),
  status: mysqlEnum("status", ["pending", "approved", "draft"]).default("draft").notNull(),
  reason: text("reason"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** weekly_timesheet_entries — 2024_12_17_112758. id() bigint PK; foreignId weekly_timesheet_id bigint. */
export const weeklyTimesheetEntries = mysqlTable("weekly_timesheet_entries", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true })
    .notNull()
    .references(() => companies.id, { onDelete: "cascade" }),
  weeklyTimesheetId: bigint("weekly_timesheet_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => weeklyTimesheets.id, { onDelete: "cascade" }),
  taskId: int("task_id", { unsigned: true }).references(() => tasks.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → tasks
  date: date("date").notNull(),
  hours: decimal("hours", { precision: 10, scale: 2 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * events — base 2018 L1017-1040. +event_link varchar nullable (2022_08_03)
 * +parent_id int unsigned self-FW CASCADE (2023_10_27) +host int unsigned →users CASCADE
 * +status enum +note varchar NOT NULL (2024_02_04) +departments text nullable (2024_08_13).
 */
export const events = mysqlTable("events", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  parentId: int("parent_id", { unsigned: true }).references((): AnyMySqlColumn => events.id, {
    onDelete: "cascade",
  }),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  eventName: varchar("event_name", { length: 255 }).notNull(),
  labelColor: varchar("label_color", { length: 255 }).notNull(),
  where: varchar("where", { length: 255 }).notNull(),
  description: mediumtext("description").notNull(),
  startDateTime: datetime("start_date_time").notNull(),
  endDateTime: datetime("end_date_time").notNull(),
  host: int("host", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  status: mysqlEnum("status", ["pending", "completed", "cancelled"]).default("pending").notNull(),
  note: varchar("note", { length: 255 }).notNull(),
  repeat: mysqlEnum("repeat", ["yes", "no"]).default("no").notNull(),
  repeatEvery: int("repeat_every"),
  repeatCycles: int("repeat_cycles"),
  repeatType: mysqlEnum("repeat_type", ["day", "week", "month", "year"]).default("day").notNull(),
  sendReminder: mysqlEnum("send_reminder", ["yes", "no"]).default("no").notNull(),
  remindTime: int("remind_time"),
  remindType: mysqlEnum("remind_type", ["day", "hour", "minute"]).default("day").notNull(),
  eventLink: varchar("event_link", { length: 255 }),
  departments: text("departments"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  eventId: text("event_id"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** event_attendees — base 2018 L1042-1051. increments PK. */
export const eventAttendees = mysqlTable(
  "event_attendees",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    eventId: int("event_id", { unsigned: true })
      .notNull()
      .references(() => events.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("event_attendees_user_id_foreign").on(t.userId),
    index("event_attendees_event_id_foreign").on(t.eventId),
  ],
);

/** event_files — 2022_08_03_101616. increments PK; event_id NOT NULL. */
export const eventFiles = mysqlTable("event_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  eventId: int("event_id", { unsigned: true })
    .notNull()
    .references(() => events.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 200 }),
  hashname: varchar("hashname", { length: 200 }),
  size: varchar("size", { length: 200 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** notices — base 2018 L1759-1772. increments PK. department_id → teams. */
export const notices = mysqlTable(
  "notices",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    to: varchar("to", { length: 255 }).default("employee").notNull(),
    heading: varchar("heading", { length: 255 }).notNull(),
    description: mediumtext("description"),
    departmentId: int("department_id", { unsigned: true }).references(() => teams.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }), // FK → teams
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("notices_department_id_foreign").on(t.departmentId)],
);

/** notice_views — base 2018 L1775-1785. bigIncrements PK. */
export const noticeViews = mysqlTable(
  "notice_views",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    noticeId: int("notice_id", { unsigned: true })
      .notNull()
      .references(() => notices.id, { onDelete: "cascade", onUpdate: "cascade" }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    read: boolean("read").default(false).notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("notice_views_notice_id_foreign").on(t.noticeId),
    index("notice_views_user_id_foreign").on(t.userId),
  ],
);

/** notice_files — 2024_09_13_114425. increments PK, no company_id. */
export const noticeFiles = mysqlTable(
  "notice_files",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    noticeId: int("notice_id", { unsigned: true })
      .notNull()
      .references(() => notices.id, { onDelete: "cascade", onUpdate: "cascade" }),
    filename: varchar("filename", { length: 255 }).notNull(),
    description: text("description"),
    googleUrl: varchar("google_url", { length: 255 }),
    hashname: varchar("hashname", { length: 255 }),
    size: varchar("size", { length: 255 }),
    dropboxLink: varchar("dropbox_link", { length: 255 }),
    externalLink: varchar("external_link", { length: 255 }),
    externalLinkName: varchar("external_link_name", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("notice_files_notice_id_foreign").on(t.noticeId)],
);

/**
 * notice_board_users — 2024_09_13_114425. bigIncrements PK, NO company_id, no timestamps.
 * Legacy reuses index names from notice_views (notice_views_notice_id_foreign /
 * notice_views_user_id_foreign) — preserved verbatim.
 */
export const noticeBoardUsers = mysqlTable(
  "notice_board_users",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    noticeId: int("notice_id", { unsigned: true })
      .notNull()
      .references(() => notices.id, { onDelete: "cascade", onUpdate: "cascade" }),
    type: mysqlEnum("type", ["employee", "client"]).default("employee").notNull(),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  },
  (t) => [
    index("notice_views_notice_id_foreign").on(t.noticeId),
    index("notice_views_user_id_foreign").on(t.userId),
  ],
);

/** client_categories — base 2018 L306-312. bigIncrements PK. */
export const clientCategories = mysqlTable("client_categories", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  categoryName: varchar("category_name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** client_sub_categories — base 2018 L332-340. bigIncrements PK. */
export const clientSubCategories = mysqlTable(
  "client_sub_categories",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    categoryId: bigint("category_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => clientCategories.id, { onDelete: "cascade", onUpdate: "cascade" }),
    categoryName: varchar("category_name", { length: 255 }).notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("client_sub_categories_category_id_foreign").on(t.categoryId)],
);

/**
 * client_contacts — base 2018 L314-330. increments PK. +client_id int unsigned →users
 * CASCADE (2024_11_05) +address text nullable (2024_12_23).
 */
export const clientContacts = mysqlTable(
  "client_contacts",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    contactName: varchar("contact_name", { length: 255 }).notNull(),
    phone: varchar("phone", { length: 255 }),
    email: varchar("email", { length: 255 }),
    title: varchar("title", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    clientId: int("client_id", { unsigned: true }).references(() => users.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    address: text("address"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("client_contacts_user_id_foreign").on(t.userId),
    index("client_contacts_client_id_index").on(t.clientId),
  ],
);

/**
 * client_details — base 2018 L342-371. increments PK. category_id → client_categories,
 * sub_category_id → client_sub_categories (both SET NULL); user_id/added_by/last_updated_by → users.
 */
export const clientDetails = mysqlTable(
  "client_details",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    companyName: varchar("company_name", { length: 255 }),
    address: text("address"),
    shippingAddress: text("shipping_address"),
    postalCode: varchar("postal_code", { length: 255 }),
    state: varchar("state", { length: 255 }),
    city: varchar("city", { length: 255 }),
    office: varchar("office", { length: 255 }),
    website: varchar("website", { length: 255 }),
    note: text("note"),
    linkedin: varchar("linkedin", { length: 255 }),
    facebook: varchar("facebook", { length: 255 }),
    twitter: varchar("twitter", { length: 255 }),
    skype: varchar("skype", { length: 255 }),
    gstNumber: varchar("gst_number", { length: 255 }),
    categoryId: bigint("category_id", { mode: "number", unsigned: true }).references(
      () => clientCategories.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    subCategoryId: bigint("sub_category_id", { mode: "number", unsigned: true }).references(
      () => clientSubCategories.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("client_details_user_id_foreign").on(t.userId)],
);

/** client_docs — base 2018 L373-388. increments PK. */
export const clientDocs = mysqlTable(
  "client_docs",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    name: varchar("name", { length: 200 }).notNull(),
    filename: varchar("filename", { length: 200 }).notNull(),
    hashname: varchar("hashname", { length: 200 }).notNull(),
    size: varchar("size", { length: 200 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("client_docs_user_id_foreign").on(t.userId)],
);

/** client_notes — base 2018 L390-407. increments PK. client_id/member_id → users CASCADE. */
export const clientNotes = mysqlTable(
  "client_notes",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    clientId: int("client_id", { unsigned: true }).references(() => users.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    title: varchar("title", { length: 255 }).notNull(),
    type: boolean("type").default(false).notNull(),
    memberId: int("member_id", { unsigned: true }).references(() => users.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    isClientShow: boolean("is_client_show").default(false).notNull(),
    askPassword: boolean("ask_password").default(false).notNull(),
    details: longtext("details").notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("client_notes_client_id_foreign").on(t.clientId),
    index("client_notes_member_id_foreign").on(t.memberId),
  ],
);

/** client_user_notes — base 2018 L410-418. increments PK. client_note_id → client_notes. */
export const clientUserNotes = mysqlTable(
  "client_user_notes",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    clientNoteId: int("client_note_id", { unsigned: true })
      .notNull()
      .references(() => clientNotes.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("client_user_notes_user_id_foreign").on(t.userId)],
);

/** dashboard_widgets — base 2018 L720-727. bigIncrements PK. */
export const dashboardWidgets = mysqlTable("dashboard_widgets", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  widgetName: varchar("widget_name", { length: 255 }).notNull(),
  status: boolean("status").default(true).notNull(),
  dashboardType: varchar("dashboard_type", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** sticky_notes — base 2018 L2567-2576. increments PK. */
export const stickyNotes = mysqlTable(
  "sticky_notes",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    noteText: mediumtext("note_text").notNull(),
    colour: mysqlEnum("colour", ["blue", "yellow", "red", "gray", "purple", "green"])
      .default("blue")
      .notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("sticky_notes_user_id_foreign").on(t.userId)],
);

/**
 * notifications — base 2018 L1787-1798. Laravel polymorphic table:
 * uuid('id')->primary() → char(36) PK; morphs('notifiable') → notifiable_type varchar +
 * notifiable_id bigint unsigned + composite index. No company_id.
 */
export const notifications = mysqlTable(
  "notifications",
  {
    id: char("id", { length: 36 }).primaryKey(),
    type: varchar("type", { length: 255 }).notNull(),
    notifiableType: varchar("notifiable_type", { length: 255 }).notNull(),
    notifiableId: bigint("notifiable_id", { mode: "number", unsigned: true }).notNull(),
    data: text("data").notNull(),
    readAt: timestamp("read_at"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("notifications_notifiable_type_notifiable_id_index").on(t.notifiableType, t.notifiableId)],
);

/** email_notification_settings — base 2018 L833-842. increments PK. */
export const emailNotificationSettings = mysqlTable("email_notification_settings", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  slug: varchar("slug", { length: 255 }),
  settingName: varchar("setting_name", { length: 255 }).notNull(),
  sendEmail: mysqlEnum("send_email", ["yes", "no"]).default("no").notNull(),
  sendSlack: mysqlEnum("send_slack", ["yes", "no"]).default("no").notNull(),
  sendPush: mysqlEnum("send_push", ["yes", "no"]).default("no").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * mention_users — 2023_04_18_124728. id() bigint PK, NO company_id. All FK cols
 * are signed integer (unsigned), nullable. +ticket_id +event_id (2023_05_16)
 * +user_chat_id (2023_05_31). Cross-batch FKs deferred (task_comments, task_notes,
 * tasks, projects, project_notes, discussions, discussion_replies, tickets, users_chat).
 */
export const mentionUsers = mysqlTable("mention_users", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  taskCommentId: int("task_comment_id", { unsigned: true }).references(() => taskComments.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → task_comments
  taskNoteId: int("task_note_id", { unsigned: true }).references(() => taskNotes.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → task_notes
  taskId: int("task_id", { unsigned: true }).references(() => tasks.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → tasks
  projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → projects
  projectNoteId: int("project_note_id", { unsigned: true }).references(() => projectNotes.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → project_notes
  discussionId: int("discussion_id", { unsigned: true }).references(() => discussions.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → discussions
  userId: int("user_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  discussionReplyId: int("discussion_reply_id", { unsigned: true }).references(
    () => discussionReplies.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ), // FK → discussion_replies
  ticketId: int("ticket_id", { unsigned: true }).references(() => tickets.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → tickets
  eventId: int("event_id", { unsigned: true }).references(() => events.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userChatId: int("user_chat_id", { unsigned: true }), // FK → users_chat in legacy, but users_chat table not ported (kept unconstrained)
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
